import { isTauri } from '@/lib/platform';

/**
 * A folder this device can list, read and write `.fdv` files in.
 *
 * Two ways to reach one, behind one shape:
 *
 *  - **The File System Access API** (Chrome, Edge, and other Chromium
 *    browsers on a desktop). The pilot picks a folder once; the browser hands
 *    back a handle, which is stored in IndexedDB like any other setting.
 *    Permission is per session unless the browser offers to remember it, so a
 *    reload can leave the handle needing one click to use again — the panel
 *    and the status line say so rather than failing quietly.
 *  - **Tauri's file system plugin**, in the desktop app. A path, picked from
 *    the native folder dialog, with no permission to lapse.
 *
 * Safari, Firefox and every iOS browser have neither. There the same files are
 * merged and saved by hand (`SyncPanel`'s manual mode) — the format is the
 * same, only the trigger differs.
 */

export interface SyncFolderFile {
  name: string;
  /** Epoch milliseconds. */
  lastModified: number;
  size: number;
}

export type FolderPermission = 'granted' | 'prompt' | 'denied';

export interface SyncFolder {
  /** What to call the folder on screen: its name, or the path in the desktop app. */
  readonly label: string;
  permission: () => Promise<FolderPermission>;
  /** Must run inside a click handler in a browser. */
  requestPermission: () => Promise<boolean>;
  /** Every `.fdv` directly inside the folder. */
  list: () => Promise<SyncFolderFile[]>;
  read: (name: string) => Promise<Uint8Array>;
  /** Replaces the file whole: another device never sees half of it. */
  write: (name: string, bytes: Uint8Array<ArrayBuffer>) => Promise<void>;
  remove: (name: string) => Promise<void>;
}

/** What is stored in the `sync.folder` setting to find the folder again. */
export type StoredFolder =
  { kind: 'fs-access'; handle: DirectoryHandle } | { kind: 'tauri'; path: string };

/*
 * The slice of the File System Access API used here. TypeScript's DOM library
 * has the handle types but not the permission methods, iteration or the
 * picker, which only Chromium ships.
 */
interface WritableStream {
  write: (data: Uint8Array<ArrayBuffer>) => Promise<void>;
  close: () => Promise<void>;
}
interface FileHandle {
  kind: 'file';
  name: string;
  getFile: () => Promise<File>;
  createWritable: () => Promise<WritableStream>;
}
export interface DirectoryHandle {
  kind: 'directory';
  name: string;
  values: () => AsyncIterable<FileHandle | DirectoryHandle>;
  getFileHandle: (name: string, options?: { create?: boolean }) => Promise<FileHandle>;
  removeEntry: (name: string) => Promise<void>;
  queryPermission: (options: { mode: 'readwrite' }) => Promise<PermissionState>;
  requestPermission: (options: { mode: 'readwrite' }) => Promise<PermissionState>;
}
type DirectoryPicker = (options: {
  id?: string;
  mode: 'readwrite';
  startIn?: string;
}) => Promise<DirectoryHandle>;

function directoryPicker(): DirectoryPicker | undefined {
  const picker = (globalThis as { showDirectoryPicker?: DirectoryPicker }).showDirectoryPicker;
  return typeof picker === 'function' ? picker : undefined;
}

/** Whether this device can keep a folder in sync on its own. */
export function supportsFolderSync(): boolean {
  return isTauri() || directoryPicker() !== undefined;
}

export function isSyncFile(name: string): boolean {
  return name.toLowerCase().endsWith('.fdv') && !name.startsWith('.');
}

function fsAccessFolder(handle: DirectoryHandle): SyncFolder {
  return {
    label: handle.name,
    permission: async () => handle.queryPermission({ mode: 'readwrite' }),
    requestPermission: async () =>
      (await handle.requestPermission({ mode: 'readwrite' })) === 'granted',
    list: async () => {
      const files: SyncFolderFile[] = [];
      for await (const entry of handle.values()) {
        if (entry.kind === 'file' && isSyncFile(entry.name)) {
          const file = await entry.getFile();
          files.push({ name: entry.name, lastModified: file.lastModified, size: file.size });
        }
      }
      return files;
    },
    read: async (name) => {
      const file = await (await handle.getFileHandle(name)).getFile();
      return new Uint8Array(await file.arrayBuffer());
    },
    write: async (name, bytes) => {
      // `createWritable` writes to a swap file and moves it into place on
      // `close`, so a reader never sees a partial archive.
      const writable = await (await handle.getFileHandle(name, { create: true })).createWritable();
      await writable.write(bytes);
      await writable.close();
    },
    remove: async (name) => {
      await handle.removeEntry(name);
    },
  };
}

async function tauriFolder(path: string): Promise<SyncFolder> {
  const fs = await import('@tauri-apps/plugin-fs');
  const { join } = await import('@tauri-apps/api/path');
  return {
    label: path,
    permission: () => Promise.resolve('granted'),
    requestPermission: () => Promise.resolve(true),
    list: async () => {
      const entries = await fs.readDir(path);
      const files: SyncFolderFile[] = [];
      for (const entry of entries) {
        if (entry.isFile && isSyncFile(entry.name)) {
          const info = await fs.stat(await join(path, entry.name));
          files.push({
            name: entry.name,
            lastModified: info.mtime?.getTime() ?? 0,
            size: info.size,
          });
        }
      }
      return files;
    },
    read: async (name) => fs.readFile(await join(path, name)),
    write: async (name, bytes) => {
      // Written beside the target under a dot-name (which `isSyncFile`
      // ignores) and renamed over it, so iCloud or OneDrive never uploads,
      // and another device never merges, half a file.
      const target = await join(path, name);
      const temporary = await join(path, `.${name}.partial`);
      await fs.writeFile(temporary, bytes);
      await fs.rename(temporary, target);
    },
    remove: async (name) => {
      await fs.remove(await join(path, name));
    },
  };
}

export async function openFolder(stored: StoredFolder): Promise<SyncFolder> {
  return stored.kind === 'tauri' ? tauriFolder(stored.path) : fsAccessFolder(stored.handle);
}

/**
 * Ask the pilot for a folder.
 *
 * @returns `null` when they cancel.
 */
export async function pickFolder(): Promise<StoredFolder | null> {
  if (isTauri()) {
    const { open } = await import('@tauri-apps/plugin-dialog');
    const path = await open({ directory: true, title: 'Choose sync folder' });
    return typeof path === 'string' ? { kind: 'tauri', path } : null;
  }
  const picker = directoryPicker();
  if (picker === undefined) {
    return null;
  }
  try {
    // `id` makes the picker reopen where it was last left.
    const handle = await picker({ id: 'fdv-sync', mode: 'readwrite', startIn: 'documents' });
    return { kind: 'fs-access', handle };
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      return null;
    }
    throw error;
  }
}
